import { memo, ReactElement } from "react";
import cn from "classnames";
import { useFormikContext } from "formik";

import styles from "./Review.module.scss";
import { getScreenByStep, impressionBase } from "../constants";

/**
 * Generates a pair of P tags with the field label and value.
 *
 * @returns {ReactElement} A pair of matching P tags title and value.
 */
const Field = (props: { title: string, value: string }): ReactElement => (
    <>
        <p className={styles.fieldTitle}>{props.title}</p>
        <p className={styles.fieldValue}>{props.value}</p>
    </>
);

/**
 * Displays a review screen for the user to confirm their application details before submitting it to The Muse.
 * It takes in the user's contact information, resume and cover letter details, and displays them to the user to review.
 *
 * @returns {ReactElement} The review screen.
 */
const Review = memo((props: {
    companyName: string;
    enabled: boolean;
}) => {
    const {
        companyName,
        enabled = false,
    } = props;
    const { values } = useFormikContext();
    const valueObj = (values as Record<string, string>);
    const screen = getScreenByStep(4);
    const impression = {
        ...impressionBase,
        sp_modal_screen_name: screen.name,
        sp_modal_screen_order: screen.step,
    };

    return (
        <div className={cn(styles.review, enabled ? styles.enabled : "")}
            data-tracking-props={JSON.stringify(impression)}
            data-tracking-event-type="impression"
        >
            <p className={styles.screenHeading}>Review Application</p>
            <p className={styles.intro}>
                {`Please review your application before submitting it to ${companyName}.
                Once you submit it you won't be able to make changes.`}
            </p>

            <div className={styles.contactGrid}>
                <div>
                    <p className={styles.screenHeading}>Contact info</p>
                    <Field title="First Name" value={valueObj.firstName} />
                    <Field title="Last Name" value={valueObj.lastName} />
                    <Field title="Email" value={valueObj.email} />
                    <Field title="Phone" value={valueObj.phone} />
                </div>
                <div>
                    <p className={styles.screenHeading}>Resume</p>
                    <p className={cn(styles.fieldValue, styles.filename)}>{valueObj.resumeName}</p>
                    <p className={styles.fileStats}>{valueObj.resumeStats}</p>

                    {valueObj.coverLetterName && <p className={styles.screenHeading}>Cover Letter</p>}
                    {valueObj.coverLetterName && <p className={cn(styles.fieldValue, styles.filename)}>{valueObj.coverLetterName}</p>}
                    {valueObj.coverLetterName && <p className={styles.fileStats}>{valueObj.coverLetterStats}</p>}
                </div>
            </div>
        </div>
    );
});

Review.displayName = "Review";
export default Review;
